import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef } from 'react'

interface Props
    extends Omit<
        React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
        'onChange' | 'onChangeCapture'
    > {
    formatter?: (value: string) => string
    parser?: (value: string) => any
    onChange?: (value: any) => void
}

const InputNumber: React.FC<Props> = ({ formatter, parser, onChange, value = '', defaultValue, ...props }) => {
    const ref = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (ref.current) {
            let formattedValue: string = value as any
            if (formatter) formattedValue = formatter(formattedValue)

            if (ref.current.value !== formattedValue) {
                ref.current.value = formattedValue
            }
        }
    }, [ref.current])

    const currentDefaultValue = useMemo(() => {
        let value: string = String(defaultValue) || ''
        if (formatter) value = formatter(value)
        return value
    }, [defaultValue, formatter])

    const onChangeCaptureHandler = useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            let value = e.currentTarget.value
            value = value.replace(/\D/g, '')
            if (formatter) value = formatter(value)
            e.currentTarget.value = value
            e.preventDefault()
        },
        [formatter]
    )

    const onChangeHandler = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            let value = e.currentTarget.value
            if (parser) value = parser(value)
            onChange && onChange(value)
            e.preventDefault()
        },
        [parser]
    )

    return (
        <input
            {...props}
            ref={ref}
            defaultValue={currentDefaultValue}
            onChangeCapture={onChangeCaptureHandler}
            onChange={onChangeHandler}
        />
    )
}

export default React.memo(InputNumber)
